import ControlledForm from "../components/form/controlled-form.tsx";
import {
  RegisterFormData,
  registerNewUserSchema,
} from "../schemas/register-new-user.schema.ts";
import { Button } from "../components/ui/button.tsx";
import { IoCheckmark } from "react-icons/io5";
import { FaTrashAlt } from "react-icons/fa";
import { useFormContext, useWatch } from "react-hook-form";
import ControlledInput from "../components/form/controlled-input.tsx";
import ControlledPassword from "../components/form/controlled-password.tsx";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toaster } from "../components/ui/toaster.tsx";
import { useEffect } from "react";
import { z } from "zod";
import { api } from "../api/custom-api.ts";

const CreateEmployee = () => {
  const {
    handleSubmit,
    formState: { isValid, isDirty },
    reset,
    trigger,
    setError,
    clearErrors,
  } = useFormContext<RegisterFormData>();

  const queryClient = useQueryClient();

  const password = useWatch({ name: "password" });
  const confirmPassword = useWatch({ name: "confirmPassword" });
  const email = useWatch({ name: "email" });

  useEffect(() => {
    const validateConfirmPassword = async () => {
      if (confirmPassword) {
        const result = await trigger("confirmPassword");

        if (result) {
          if (password !== confirmPassword) {
            setError("confirmPassword", {
              message: "Šifre se ne podudaraju",
            });
          } else {
            clearErrors("confirmPassword");
          }
        }
      }
    };

    validateConfirmPassword();
  }, [confirmPassword, password, trigger, setError, clearErrors]);

  const { mutate } = useMutation({
    // @ts-ignore
    mutationFn: (request: any) => api.auth.registrationWarehouse(request),
    onSuccess: () => {
      toaster.create({
        title: "Kreiran novi korisnik!",
        // @ts-expect-error
        placement: "bottom-end",
        description: "Novi korisnik je uspešno kreiran",
        type: "info",
        duration: 5000,
      });
      reset({
        email: "",
        password: "",
        confirmPassword: "",
        firstName: "",
        lastName: "",
      });
      queryClient.invalidateQueries(["users"]);
    },
    onError: () => {
      toaster.create({
        title: "Greška!",
        // @ts-expect-error
        placement: "bottom-end",
        description: "Proverite unete podatke",
        type: "error",
        duration: 5000,
      });
    },
  });

  const { mutate: checkEmailExist } = useMutation({
    mutationFn: (email: string) => api.user.checkEmail({ email }),
    onSuccess: (response) => {
      if (response.exist) {
        setError("email", {
          message: "Korisnik sa zadatom email adresom već postoji",
        });
      }
    },
    onError: () => {
      setError("email", {
        message: "Korisnik sa zadatom email adresom već postoji",
      });
    },
  });

  useEffect(() => {
    if (!email) {
      return;
    }

    const timeout = setTimeout(() => {
      const emailSchema = z.string().email();

      const validationResult = emailSchema.safeParse(email);

      if (validationResult.success) {
        checkEmailExist(email);
      } else {
        return;
      }
    }, 1000);

    return () => clearTimeout(timeout);
  }, [email, checkEmailExist]);

  const onSubmit = handleSubmit((data) => {
    mutate({
      email: data.email,
      password: data.password,
      firstName: data.firstName,
      lastName: data.lastName,
    });
  });

  // @ts-ignore
  return (
    <div className="pt-[5.25rem] h-full xl:h-full p-[1rem] bg-[#ffffff] flex flex-col items-center">
      <div className="w-full flex flex-col sm:flex-row justify-between items-center px-[0.25rem]">
        <div className="flex flex-row gap-[1rem] items-center">
          <span className="text-[24px] text-center text-black font-semibold">
            KREIRAJ NOVOG ZAPOSLENOG
          </span>
        </div>
        <div className="flex flex-row gap-[1rem] items-center">
          <div className="w-[10rem] sm:w-[12rem]">
            <Button
              size={"sm"}
              colorPalette={"black"}
              variant={"surface"}
              width={"full"}
              color={"#000000"}
              backgroundColor={"#ffffff"}
              borderColor={"#ffffff"}
              fontWeight={"600"}
              fontSize={"14px"}
              borderRadius={"0.5rem"}
              disabled={!isValid}
              onClick={onSubmit}
            >
              <IoCheckmark /> Potvrdi
            </Button>
          </div>
          <div className="w-[10rem] sm:w-[12rem]">
            <Button
              size={"sm"}
              colorPalette={"black"}
              variant={"surface"}
              width={"full"}
              color={"#000000"}
              backgroundColor={"#ffffff"}
              borderColor={"#ffffff"}
              fontWeight={"600"}
              fontSize={"14px"}
              borderRadius={"0.5rem"}
              disabled={!isDirty}
              onClick={() => {
                reset({
                  email: "",
                  password: "",
                  confirmPassword: "",
                  firstName: "",
                  lastName: "",
                });
              }}
            >
              <FaTrashAlt /> Poništi
            </Button>
          </div>
        </div>
      </div>
      <div className="w-full h-full flex flex-col gap-[1rem] mt-[1rem] items-center overflow-scroll">
        <div className="w-full flex flex-col items-center gap-[1rem] justify-between mt-[1rem] px-[0.25rem]">
          <ControlledInput
            name={"email"}
            className="w-full sm:w-[24rem] md:w-[28rem]"
            label={"Email adresa"}
            placeholder={"Unesi email adresu"}
          />
          <ControlledPassword
            name={"password"}
            className="w-full sm:w-[24rem] md:w-[28rem]"
            label={"Šifra"}
            placeholder={"Unesi šifru"}
          />
          <ControlledPassword
            name={"confirmPassword"}
            className="w-full sm:w-[24rem] md:w-[28rem]"
            label={"Potvrda šifre"}
            placeholder={"Unesi potvrdu šifre"}
          />
          <ControlledInput
            name={"firstName"}
            className="w-full sm:w-[24rem] md:w-[28rem]"
            label={"Ime korisnika"}
            placeholder={"Unesi ime korisnika"}
          />
          <ControlledInput
            name={"lastName"}
            className="w-full sm:w-[24rem] md:w-[28rem]"
            label={"Prezime korisnika"}
            placeholder={"Unesi prezime korisnika"}
          />
        </div>
      </div>
    </div>
  );
};

export const CreateEmployeePage = () => {
  return (
    <ControlledForm schema={registerNewUserSchema}>
      <CreateEmployee />
    </ControlledForm>
  );
};
